(() => {
  const STORAGE_KEY = 'discountKitActiveCodes';
  const UTM_STORAGE_KEY = 'readersCampaignUtmCampaigns';
  const ATTRIBUTE_NAME = 'dk_active_discount_code';
  const REMOVED_PREFIX = 'fgDkDiscountCodeRemoved:';
  const SESSION_EVENT_NAME = 'readers:campaign-session-change';
  const FG_SESSION_EVENT_NAME = 'fg:discount-kit-session-change';
  const DISCOUNT_PATH_PATTERN = /\/discount\/([^/?#]+)/i;

  /**
   * Normalizes a campaign token for case-insensitive comparisons.
   * @param {string} value Campaign token to normalize.
   * @returns {string}
   */
  function normalizeToken(value) {
    return String(value || '').trim().toLowerCase();
  }

  /**
   * Reads a normalized string array from session storage.
   * @param {string} key Session storage key.
   * @returns {string[]}
   */
  function getStoredTokens(key) {
    try {
      const storedValue = window.sessionStorage.getItem(key);
      const parsedTokens = storedValue ? JSON.parse(storedValue) : [];

      return Array.isArray(parsedTokens) ? parsedTokens.map(normalizeToken).filter(Boolean) : [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Persists normalized campaign tokens for the current browser session.
   * @param {string} key Session storage key.
   * @param {string[]} values Values to store.
   * @returns {string[]}
   */
  function setStoredTokens(key, values) {
    const normalizedValues = Array.from(new Set((values || []).map(normalizeToken).filter(Boolean)));

    try {
      if (normalizedValues.length > 0) {
        window.sessionStorage.setItem(key, JSON.stringify(normalizedValues));
      } else {
        window.sessionStorage.removeItem(key);
      }
    } catch (error) {
      return normalizedValues;
    }

    return normalizedValues;
  }

  /**
   * Checks whether a code was explicitly removed in this browser session.
   * @param {string} value Discount code to inspect.
   * @returns {boolean}
   */
  function hasRemovalGuard(value) {
    const normalizedValue = normalizeToken(value);
    if (!normalizedValue) return false;

    try {
      return Boolean(
        window.sessionStorage.getItem(`${REMOVED_PREFIX}${normalizedValue.toUpperCase()}`) ||
          window.sessionStorage.getItem(`${REMOVED_PREFIX}${normalizedValue}`)
      );
    } catch (error) {
      return false;
    }
  }

  /**
   * Removes codes that the shopper explicitly cleared from the cart form.
   * @param {string[]} values Discount codes to filter.
   * @returns {string[]}
   */
  function filterRemovedTokens(values) {
    return (values || []).filter((value) => !hasRemovalGuard(value));
  }

  /**
   * Extracts discount codes from a URL path or query string.
   * @param {string} urlValue URL to inspect.
   * @returns {string[]}
   */
  function getDiscountCodesFromUrl(urlValue) {
    const codes = [];

    try {
      const url = new URL(urlValue, window.location.origin);
      const discountPathMatch = url.pathname.match(DISCOUNT_PATH_PATTERN);

      if (discountPathMatch?.[1]) {
        codes.push(decodeURIComponent(discountPathMatch[1]));
      }

      ['discount', 'discount_code', 'promo_code', 'code'].forEach((paramName) => {
        const paramValue = url.searchParams.get(paramName);

        if (paramValue) {
          codes.push(paramValue);
        }
      });
    } catch (error) {
      return codes;
    }

    return codes.map(normalizeToken).filter(Boolean);
  }

  /**
   * Extracts UTM campaigns from a URL.
   * @param {string} urlValue URL to inspect.
   * @returns {string[]}
   */
  function getUtmCampaignsFromUrl(urlValue) {
    const campaigns = [];

    try {
      const url = new URL(urlValue, window.location.origin);
      const campaignValue = url.searchParams.get('utm_campaign');

      if (campaignValue) {
        campaigns.push(campaignValue);
      }
    } catch (error) {
      return campaigns;
    }

    return campaigns.map(normalizeToken).filter(Boolean);
  }

  /**
   * Reads an existing cookie value.
   * @param {string} name Cookie name.
   * @returns {string}
   */
  function getCookie(name) {
    try {
      const needle = `${encodeURIComponent(name)}=`;
      const parts = document.cookie.split(';');
      const cookie = parts.map((part) => part.trim()).find((part) => part.indexOf(needle) === 0);

      return cookie ? decodeURIComponent(cookie.substring(needle.length)) : '';
    } catch (error) {
      return '';
    }
  }

  /**
   * Builds the public campaign session API consumed by storefront components.
   * @param {string[]} codes Active discount codes.
   * @param {string[]} campaigns Active UTM campaigns.
   * @returns {Object}
   */
  function createCampaignSession(codes, campaigns) {
    const discountCodes = Array.from(new Set(filterRemovedTokens(codes).map(normalizeToken).filter(Boolean)));
    const utmCampaigns = Array.from(new Set((campaigns || []).map(normalizeToken).filter(Boolean)));

    return {
      discountCodes,
      utmCampaigns,
      primaryUtmCampaign: utmCampaigns[0] || '',
      normalize: normalizeToken,
      hasDiscountCode(code) {
        return discountCodes.includes(normalizeToken(code));
      },
      hasUtmCampaign(campaign) {
        const normalizedCampaign = normalizeToken(campaign);
        return normalizedCampaign ? utmCampaigns.includes(normalizedCampaign) : utmCampaigns.length > 0;
      },
    };
  }

  /**
   * Publishes the campaign session to the window and notifies listeners.
   * @param {string[]} codes Active discount codes.
   * @param {string[]} campaigns Active UTM campaigns.
   * @returns {Object}
   */
  function publishCampaignSession(codes, campaigns) {
    const session = createCampaignSession(codes, campaigns);

    window.readersCampaignSession = session;
    window.fgDiscountKitSession = session;
    session.refresh = refreshCampaignSession;

    document.dispatchEvent(new CustomEvent(SESSION_EVENT_NAME, { detail: session }));
    document.dispatchEvent(new CustomEvent(FG_SESSION_EVENT_NAME, { detail: session }));

    return session;
  }

  /**
   * Shows session-only Discount Kit markup when its code is active.
   * @param {string[]} codes Active discount codes.
   */
  function applyDiscountKitSessionDisplays(codes) {
    const activeCodes = new Set(filterRemovedTokens(codes).map(normalizeToken).filter(Boolean));

    document.querySelectorAll('[data-discount-kit-session-only][data-discount-code-lower]').forEach((element) => {
      const code = normalizeToken(element.getAttribute('data-discount-code-lower'));
      const isActive = activeCodes.has(code);

      element.hidden = !isActive;
      element.classList.toggle('tw-hidden', !isActive);
    });

    document.querySelectorAll('.badge__wrapper').forEach((wrapper) => {
      const hasActiveSessionBadge = Array.from(
        wrapper.querySelectorAll('.discount-kit-badge[data-discount-kit-session-only][data-discount-code-lower]')
      ).some((badge) => activeCodes.has(normalizeToken(badge.getAttribute('data-discount-code-lower'))));

      wrapper.querySelectorAll('.discount-kit-badge:not([data-discount-kit-session-only])').forEach((badge) => {
        badge.hidden = hasActiveSessionBadge;
        badge.classList.toggle('tw-hidden', hasActiveSessionBadge);
      });
    });

    document.querySelectorAll('[data-discount-kit-code-price][data-discount-code-lower]').forEach((container) => {
      const code = normalizeToken(container.getAttribute('data-discount-code-lower'));
      const isActive = activeCodes.has(code);
      const regularPrice = container.querySelector('[data-discount-kit-regular-price]');

      if (regularPrice) {
        regularPrice.hidden = isActive;
        regularPrice.classList.toggle('tw-hidden', isActive);
      }
    });

    document.querySelectorAll('[data-discount-kit-default-price][data-discount-code-lower]').forEach((container) => {
      const code = normalizeToken(container.getAttribute('data-discount-code-lower'));
      const isActive = activeCodes.has(code);

      container.hidden = isActive;
      container.classList.toggle('tw-hidden', isActive);
    });
  }

  /**
   * Returns a Shopify Ajax API URL with the .js suffix.
   * @param {string} route Route from Shopify's global routes object.
   * @param {string} fallback Fallback Ajax route.
   * @returns {string}
   */
  function getAjaxRoute(route, fallback) {
    const url = route || fallback;
    return url.endsWith('.js') ? url : `${url}.js`;
  }

  /**
   * Applies active codes to Shopify's cart session and mirrors the first code in
   * the cart attribute used by the local cart discount form.
   * @param {string[]} codes Active discount codes.
   */
  function applyCodesToCart(codes) {
    const normalizedCodes = filterRemovedTokens(codes).map(normalizeToken).filter(Boolean);

    if (normalizedCodes.length === 0) {
      return;
    }

    const body = new URLSearchParams();
    body.set('discount', normalizedCodes.join(','));
    body.set(`attributes[${ATTRIBUTE_NAME}]`, normalizedCodes[0]);

    window
      .fetch(getAjaxRoute(window.routes?.cart_update_url, '/cart/update'), {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
      })
      .catch(() => {});
  }

  /**
   * Reads Shopify cart discount codes that were stored by /discount links.
   * @returns {Promise<string[]>}
   */
  function getDiscountCodesFromCart() {
    return window
      .fetch(getAjaxRoute(window.routes?.cart_url, '/cart'), { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => {
        if (!cart) return { valid: [], expired: [] };

        const hasItems = cart.item_count > 0;
        const rawCodes = Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
        const valid = [];
        const expired = [];

        rawCodes.forEach((discount) => {
          const code = discount?.code;
          if (!code) return;

          // On an empty cart every code reads applicable:false because there is nothing to apply
          // it to, so keep it. On a non-empty cart applicable:false means Discount Kit produced no
          // discount for the code (expired/invalid), so it must not be re-applied or shown.
          if (hasItems && discount.applicable === false) {
            expired.push(code);
          } else {
            valid.push(code);
          }
        });

        const attributeCode = cart.attributes?.[ATTRIBUTE_NAME];
        if (attributeCode && !expired.some((code) => normalizeToken(code) === normalizeToken(attributeCode))) {
          valid.push(attributeCode);
        }

        return { valid, expired };
      })
      .catch(() => ({ valid: [], expired: [] }));
  }

  /**
   * Refreshes active campaign state from URL, referrer, storage, cookies and cart data.
   * @returns {Promise<Object>}
   */
  function refreshCampaignSession() {
    const initialCodes = filterRemovedTokens([
      ...getStoredTokens(STORAGE_KEY),
      ...getDiscountCodesFromUrl(window.location.href),
      ...getDiscountCodesFromUrl(document.referrer),
      getCookie('discount_code'),
    ]);
    const initialCampaigns = [
      ...getStoredTokens(UTM_STORAGE_KEY),
      ...getUtmCampaignsFromUrl(window.location.href),
      ...getUtmCampaignsFromUrl(document.referrer),
      getCookie('utm'),
      getCookie('utm_campaign_cached'),
    ];

    let activeCodes = setStoredTokens(STORAGE_KEY, initialCodes);
    let activeCampaigns = setStoredTokens(UTM_STORAGE_KEY, initialCampaigns);

    publishCampaignSession(activeCodes, activeCampaigns);
    applyDiscountKitSessionDisplays(activeCodes);
    applyCodesToCart(activeCodes);

    return getDiscountCodesFromCart().then(({ valid: cartCodes, expired: expiredCodes }) => {
      activeCodes = setStoredTokens(STORAGE_KEY, filterRemovedTokens(activeCodes.concat(cartCodes)));

      if (expiredCodes.length > 0) {
        // An expired/invalid code was optimistically applied; drop it from the active set and
        // rewrite the cart so it stops reaching checkout as a phantom label and never renders as
        // an active promo.
        activeCodes = setStoredTokens(
          STORAGE_KEY,
          activeCodes.filter(
            (code) => !expiredCodes.some((expiredCode) => normalizeToken(expiredCode) === normalizeToken(code))
          )
        );

        const body = new URLSearchParams();
        body.set('discount', activeCodes.join(','));
        body.set(`attributes[${ATTRIBUTE_NAME}]`, activeCodes[0] || '');
        window
          .fetch(getAjaxRoute(window.routes?.cart_update_url, '/cart/update'), {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body,
          })
          .catch(() => {});
      }

      activeCampaigns = setStoredTokens(UTM_STORAGE_KEY, activeCampaigns);
      const session = publishCampaignSession(activeCodes, activeCampaigns);

      applyDiscountKitSessionDisplays(activeCodes);

      return session;
    });
  }

  window.readersCampaignSession = createCampaignSession(
    filterRemovedTokens([
      ...getStoredTokens(STORAGE_KEY),
      ...getDiscountCodesFromUrl(window.location.href),
      ...getDiscountCodesFromUrl(document.referrer),
      getCookie('discount_code'),
    ]),
    [
      ...getStoredTokens(UTM_STORAGE_KEY),
      ...getUtmCampaignsFromUrl(window.location.href),
      ...getUtmCampaignsFromUrl(document.referrer),
      getCookie('utm'),
      getCookie('utm_campaign_cached'),
    ]
  );
  window.fgDiscountKitSession = window.readersCampaignSession;
  window.readersCampaignSession.refresh = refreshCampaignSession;
  window.fgDiscountKitSession.refresh = refreshCampaignSession;
  refreshCampaignSession();

  document.addEventListener('DOMContentLoaded', () => {
    applyDiscountKitSessionDisplays(getStoredTokens(STORAGE_KEY));

    const observer = new MutationObserver(() => {
      applyDiscountKitSessionDisplays(getStoredTokens(STORAGE_KEY));
    });

    observer.observe(document.body, { childList: true, subtree: true });
  });
})();
